import React from 'react';
import { echartsContent, getPercentageInfo } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { chartAllColors } from '@/pages/opinion/constants';
import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import { reviewsFormat, sentimentFormat } from '@/pages/opinion/utils/value';
import {
  formatTimeXAxisText,
  getChartPosition,
} from '@/pages/opinion/utils/chart';
import { othersList } from '@op/pages/stats/constants';

export default function ChartCommonLineBar({
  chartData,
  xAxisData,
  filter,
  formatMessage,
  height = 300,
  loading,
  showTitle = true,
  chartType = 'line',
  dataType = 'Mentions',
  handleOnClick,
  transFunc = (name: string) => name,
  timelineTotal,
  isVersion = false,
  leftTitle = null,
  rightTitle = null,
  tips = '',
  isTopic,
  formatValue: propFormatValue,
  showShare = true,
  showSumText = true,
  yAxisFormat,
}: any) {
  const formatValue =
    propFormatValue ||
    (dataType === 'Mentions'
      ? (value: number) => reviewsFormat(value, 2)
      : (value: number) => sentimentFormat(value, '-'));

  const legendData: Array<any> = [];
  const newCharData = chartData?.map((v: any, i: number) => {
    if (isTopic && othersList.includes(v.name)) return;
    const color = chartAllColors[i];
    legendData.push({
      name: transFunc(v.name),
      icon: 'circle',
      itemStyle: {
        color: color,
      },
    });

    let item: any = {
      data: v.data,
      name: transFunc(v.name),
      originName: v.name,
      type: chartType,
    };
    if (chartType === 'bar') {
      item['stack'] = 'Total';
    }
    return item;
  });

  // console.log(newCharData, 'newCharData');

  const options: any = {
    color: chartAllColors,
    tooltip: {
      appendToBody: false,
      confine: false,
      position: (pos: any, params: any, dom: any, rect: any, size: any) => {
        return getChartPosition(pos, params, dom, rect, size, -5);
      },
      formatter: (params: any) => {
        let currentDateTotal = 0;
        params?.forEach((item: any) => (currentDateTotal += item?.value));
        let sumText = formatMessage({ id: 'Sum' });
        if (timelineTotal) {
          currentDateTotal = timelineTotal[params[0]?.name] || currentDateTotal;
          sumText = `${sumText} (${formatMessage({
            id: 'Removed duplicate reviews',
          })})`;
        }
        if (tips) {
          sumText = `${sumText} (${formatMessage({ id: tips })})`;
        }
        return formatChartParamsDate({
          params,
          total: currentDateTotal,
          isSort: true,
          valueFormat: formatValue,
          dealwithRecords(records, total) {
            let newRecords: Array<any> = records.map((item) => {
              if (isTopic && !item.originValue) return null;
              const share = getPercentageInfo(item.originValue, total, 2);
              return {
                ...item,
                share: share === '-' || share === '0%' ? '-' : share,
              };
            });
            if (dataType !== 'Sentiment' && !isTopic && showSumText) {
              newRecords.unshift({
                text: sumText,
                value: formatValue(total),
                share: '100%',
              });
            }

            return newRecords.filter((item) => Boolean(item));
          },
          tablesConfigFunc: (records: any) => {
            let columns = [
              {
                key: 'text',
                header: isVersion
                  ? params[0]?.name
                  : listTimeTitleFunc(params[0]?.name, filter.date_type),
              },
              {
                key: 'value',
                align: 'right',
                weight: 'bold',
                header: formatMessage({ id: dataType }),
              },
            ];
            if (dataType !== 'Sentiment' && showShare) {
              columns.push({
                key: 'share',
                align: 'right',
                weight: 'bold',
                header: formatMessage({ id: 'Share-1' }),
              });
            }

            return [
              {
                showHeader: true,
                records,
                columns,
              },
            ];
          },
        });
      },
    },
    legend: {
      data: legendData,
    },
    axisPointer: getAxisPointer((data) => {
      return isVersion
        ? data.value
        : formatTimeXAxisText(data.value, filter.date_type);
    }),
    xAxis: {
      type: 'category',
      axisLabel: {
        formatter: (value: string) =>
          isVersion ? value : formatTimeXAxisText(value, filter.date_type),
      },
      boundaryGap: ['20%', 0],
      data: xAxisData,
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: any) => {
          return yAxisFormat ? yAxisFormat(value) : echartsContent(value);
        },
      },
    },
    series: newCharData,
  };

  if (showTitle) {
    options['title'] = {
      text: formatMessage({ id: 'Mentions' }),
      show: dataType === 'Mentions',
      textStyle: {
        fontWeight: '400',
        fontSize: 12,
        color: '#444444',
      },
    };
  }

  const hasChartCommonTitle = leftTitle || rightTitle;

  return (
    <>
      <EChartCommon
        optionsProps={{ type: chartType }}
        empty={!chartData || chartData.length === 0}
        loading={loading}
        options={options}
        leftTitle={leftTitle}
        rightTitle={rightTitle}
        height={hasChartCommonTitle ? height - 34 : height}
        eventsDict={{
          click: (params: any) => {
            // console.log(params);
            handleOnClick?.(params);
          },
        }}
      />
    </>
  );
}
